/** Behaviour shared by the bench's context menus, so they can't drift apart. */
import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/** Close on a click outside `ref`, or on Escape. */
export function useDismiss(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  useEffect(() => {
    function handlePointerDown(e: PointerEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [ref, onClose]);
}

const VIEWPORT_MARGIN = 8;

/**
 * Keeps a menu anchored at the click point but fully on screen — right-clicking
 * a vessel near the bottom of the bench would otherwise open a menu whose lower
 * half is cut off by the window.
 *
 * useLayoutEffect, not useEffect: the correction is measured from the rendered
 * menu, and it has to land before the browser paints or the menu visibly jumps.
 */
export function useAnchoredPosition(
  ref: RefObject<HTMLElement | null>, x: number, y: number,
): { left: number; top: number } {
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.max(VIEWPORT_MARGIN, Math.min(x, window.innerWidth - width - VIEWPORT_MARGIN)),
      top: Math.max(VIEWPORT_MARGIN, Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN)),
    });
  }, [ref, x, y]);

  return pos;
}
